import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { makeOwnerGate } from '../lib/ownerGate';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { BrandLoader } from '../components/BrandLoader';
import { requestsApi, specialtiesApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Plus, 
  Search, 
  Filter,
  FileText,
  Calendar,
  Building2,
  FlaskConical,
  Lock
} from 'lucide-react';
import { getStatusColor, getStatusLabel, getTypeColor, formatDate, truncateText } from '../lib/utils';

export default function RequestsList() {
  const { isCompanyRep, isCompanyOwner } = useAuth();
  const blockIfOwner = makeOwnerGate(isCompanyOwner);
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    type: '',
    status: '',
    search: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [requestsRes, specsRes] = await Promise.all([
        requestsApi.getAll(),
        specialtiesApi.getAll()
      ]);
      setRequests(requestsRes.data);
      setSpecialties(specsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredRequests = requests.filter(request => {
    // iter163e — Company / Company Rep accounts should NEVER see locked (restricted)
    // requests in their list — not even as a "Restricted — not visible to your account"
    // placeholder row. They get a clean list of only the requests they own/can act on.
    if (request.is_locked && (isCompanyRep() || isCompanyOwner())) return false;
    if (filters.type && request.type !== filters.type) return false;
    if (filters.status && request.status !== filters.status) return false;
    if (filters.search) {
      const search = filters.search.toLowerCase();
      return (
        request.title?.toLowerCase().includes(search) ||
        request.ref_no?.toLowerCase().includes(search) ||
        request.company_name?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const statuses = [
    'DRAFT', 'SUBMITTED', 'ROUTED_TO_CONSULTANTS', 'IN_PROGRESS',
    'AWAITING_CHAIRMAN_ASSIGNMENT', 'COORDINATOR_ASSIGNED', 'INVESTIGATORS_ASSIGNED',
    'REVIEWERS_ASSIGNED', 'UNDER_REVIEW', 'CHANGES_REQUESTED', 'RESUBMITTED',
    'PENDING_FINAL_APPROVAL', 'APPROVED', 'PENDING_DIRECTOR_REVIEW', 
    'PENDING_EXEC_DIRECTOR_REVIEW', 'PENDING_SIGNATURES', 'EXECUTED',
    'REJECTED', 'COMPLETED', 'SIGNED', 'FAILED', 'WITHDRAWN'
  ];

  return (
    <DashboardLayout title="Requests">
      <div className="space-y-6" data-testid="requests-list">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 font-['Manrope']">
              {isCompanyRep() ? 'My Requests' : 'All Requests'}
            </h2>
            <p className="text-slate-500 mt-1">
              {filteredRequests.length} request{filteredRequests.length !== 1 ? 's' : ''} found
            </p>
          </div>
          {isCompanyRep() && (
            <Button 
              onClick={() => { if (blockIfOwner()) return; navigate('/requests/new'); }}
              className="bg-blue-900 hover:bg-blue-950"
              data-testid="new-request-btn"
            >
              <Plus size={16} className="mr-2" />
              New Request
            </Button>
          )}
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search by title, reference..."
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    className="pl-10"
                    data-testid="search-input"
                  />
                </div>
              </div>
              <Select 
                value={filters.type || "all"} 
                onValueChange={(value) => setFilters({ ...filters, type: value === "all" ? "" : value })}
              >
                <SelectTrigger className="w-[140px]" data-testid="type-filter">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="FEA">Feasibility</SelectItem>
                  <SelectItem value="CTA">CTA</SelectItem>
                </SelectContent>
              </Select>
              <Select 
                value={filters.status || "all"} 
                onValueChange={(value) => setFilters({ ...filters, status: value === "all" ? "" : value })}
              >
                <SelectTrigger className="w-[180px]" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {statuses.map(status => (
                    <SelectItem key={status} value={status}>
                      {getStatusLabel(status)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(filters.type || filters.status || filters.search) && (
                <Button 
                  variant="ghost" 
                  onClick={() => setFilters({ type: '', status: '', search: '' })}
                >
                  Clear filters
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Requests List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <BrandLoader size="md" label="Loading requests…" />
          </div>
        ) : filteredRequests.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700">No requests found</h3>
              <p className="text-slate-500 mt-1">
                {filters.search || filters.type || filters.status 
                  ? 'Try adjusting your filters'
                  : 'Create your first request to get started'}
              </p>
              {isCompanyRep() && !filters.search && !filters.type && !filters.status && (
                <Button 
                  onClick={() => navigate('/requests/new')}
                  className="mt-4 bg-blue-900 hover:bg-blue-950"
                >
                  Create Request
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {filteredRequests.map((request, index) => (
              <Card 
                key={request.id}
                className={`card-hover animate-slide-up min-w-0 overflow-hidden ${request.is_locked ? 'opacity-60 cursor-not-allowed bg-slate-50 border-dashed' : 'cursor-pointer'}`}
                style={{ animationDelay: `${index * 0.05}s` }}
                onClick={() => {
                  if (request.is_locked) {
                    return;
                  }
                  navigate(`/requests/${request.id}`);
                }}
                data-testid={request.is_locked ? `request-item-locked-${request.id}` : `request-item-${request.id}`}
              >
                <CardContent className="p-5">
                  {request.is_locked && (
                    <div className="mb-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-[10px] text-amber-700" data-testid="locked-badge">
                      <Lock className="w-3 h-3" /> Restricted — not visible to your account
                    </div>
                  )}
                  <div className="flex items-start gap-4 min-w-0">
                    <div className={`w-12 h-12 rounded-lg ${request.type === 'FEA' ? 'bg-teal-100' : (request.type === 'CTA' ? 'bg-violet-100' : 'bg-emerald-100')} flex items-center justify-center flex-shrink-0`}>
                      <FileText className={`w-6 h-6 ${request.type === 'FEA' ? 'text-teal-600' : (request.type === 'CTA' ? 'text-violet-600' : 'text-emerald-600')}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 min-w-0">
                        <Badge className={`${getTypeColor(request.type)} flex-shrink-0`}>
                          {request.type}
                        </Badge>
                        <span className="text-sm font-mono text-slate-500 truncate">
                          {request.ref_no}
                        </span>
                      </div>
                      <h3 className={`font-semibold truncate ${request.is_locked ? 'text-slate-500' : 'text-slate-900'}`}>
                        {request.is_locked ? request.title : request.title}
                      </h3>
                      {!request.is_locked && (
                      <p className="text-sm text-slate-500 mt-1 line-clamp-2 break-words">
                        {truncateText(request.description, 120)}
                      </p>
                      )}
                      <div className="flex items-center gap-4 mt-3 text-xs text-slate-400 flex-wrap min-w-0">
                        {request.company_name && (
                          <span className="flex items-center gap-1 min-w-0 max-w-full">
                            <Building2 size={12} className="flex-shrink-0" />
                            <span className="truncate">{request.company_name}</span>
                          </span>
                        )}
                        {request.specialty_names && request.specialty_names.length > 0 && (
                          <span className="flex items-center gap-1 min-w-0 max-w-full">
                            <FlaskConical size={12} className="flex-shrink-0" />
                            <span className="truncate">{request.specialty_names.join(', ')}</span>
                          </span>
                        )}
                        <span className="flex items-center gap-1 flex-shrink-0">
                          <Calendar size={12} />
                          {formatDate(request.created_at)}
                        </span>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <Badge className={getStatusColor(request.status)}>
                        {getStatusLabel(request.status, request.type)}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
